// One poll of the Hall's online list, shared by every panel that shows
// who is online: the Hall's Online tab renders the rows and the Tavern
// header shows their count, so the two can never disagree.
import { onConnectionState } from '../serverClient';
import { getOnlinePlayers } from './matchmakingApi';

const POLL_MS = 15_000;

let state = { rows: [], loaded: false };
let subscribers = new Set();
let mounts = 0;
let timer = null;
let unsubConnection = null;
let inflight = null;
let queued = false;

function emit() { for (const cb of subscribers) cb(state); }

export function getState() { return state; }

async function fetchOnce() {
  try {
    const data = await getOnlinePlayers();
    if (!Array.isArray(data?.rows)) return;
    state = { rows: data.rows, loaded: true };
    emit();
  } catch (err) {
    console.warn('[onlinePlayers] refresh failed:', err?.message || err);
  }
}

// A refresh asked for while one is in flight runs once more after it,
// never in parallel: the answer already on the wire may predate what
// prompted the ask (the socket registering, the Hall mounting).
export function refresh() {
  if (inflight) { queued = true; return inflight; }
  inflight = fetchOnce().finally(() => {
    inflight = null;
    if (queued) { queued = false; refresh(); }
  });
  return inflight;
}

// Ref-counted: the poll runs while at least one subscriber is mounted.
// A new subscriber gets the last rows at once and a fresh fetch right
// behind them; the Tavern holds a subscription for the whole session,
// so the Hall mounting later must not wait for the next poll tick.
// The server builds the list from its live sockets, and the Tavern's
// first fetch leaves before ours has registered, so the socket opening
// is a refresh too; otherwise the player is missing from their own
// list until the next tick.
export function subscribe(cb) {
  subscribers.add(cb);
  if (state.loaded) cb(state);
  if (mounts++ === 0) {
    timer = setInterval(refresh, POLL_MS);
    unsubConnection = onConnectionState((s) => { if (s === 'open') refresh(); });
  }
  refresh();
  return () => {
    subscribers.delete(cb);
    if (--mounts === 0) {
      clearInterval(timer); timer = null;
      unsubConnection?.(); unsubConnection = null;
    }
  };
}

export function _resetForTests() {
  clearInterval(timer); timer = null; unsubConnection?.(); unsubConnection = null;
  mounts = 0; subscribers = new Set();
  state = { rows: [], loaded: false }; inflight = null; queued = false;
}
